import { useEffect, useRef, useState } from "react";

// Circular wind-direction dial. value/onChange in degrees, "from" clock:
// 0 = 12 o'clock (headwind), 90 = 3 o'clock (right), 180 = 6, 270 = 9.
// Click or drag anywhere on the dial to set the direction the wind comes FROM.
const SZ = 148;
const C = SZ / 2;
const RIM = 60;

export const WindDial = ({ value, onChange }) => {
  const ref = useRef(null);
  const [dragging, setDragging] = useState(false);

  const setFromEvent = (clientX, clientY) => {
    const rect = ref.current.getBoundingClientRect();
    const cx = rect.left + rect.width / 2;
    const cy = rect.top + rect.height / 2;
    const dx = clientX - cx;
    const dy = clientY - cy;
    let deg = (Math.atan2(dx, -dy) * 180) / Math.PI; // top = 0, clockwise
    if (deg < 0) deg += 360;
    onChange(Math.round(deg));
  };

  useEffect(() => {
    if (!dragging) return;
    const move = (e) => {
      const t = e.touches ? e.touches[0] : e;
      setFromEvent(t.clientX, t.clientY);
    };
    const up = () => setDragging(false);
    window.addEventListener("mousemove", move);
    window.addEventListener("mouseup", up);
    window.addEventListener("touchmove", move, { passive: false });
    window.addEventListener("touchend", up);
    return () => {
      window.removeEventListener("mousemove", move);
      window.removeEventListener("mouseup", up);
      window.removeEventListener("touchmove", move);
      window.removeEventListener("touchend", up);
    };
  }, [dragging]); // eslint-disable-line react-hooks/exhaustive-deps

  const rad = (value * Math.PI) / 180;
  // Source point on the rim (where wind comes from)
  const sx = C + Math.sin(rad) * RIM;
  const sy = C - Math.cos(rad) * RIM;
  // Wind flows from source through the centre toward the opposite side
  const ox = C - Math.sin(rad) * (RIM - 14);
  const oy = C + Math.cos(rad) * (RIM - 14);

  const hour = Math.round(value / 30) % 12;
  const clock = `${hour === 0 ? 12 : hour}h`;
  const marks = [
    { label: "12", x: C, y: 16 },
    { label: "3", x: SZ - 12, y: C + 4 },
    { label: "6", x: C, y: SZ - 8 },
    { label: "9", x: 12, y: C + 4 },
  ];

  return (
    <div className="flex flex-col items-center">
      <svg
        ref={ref}
        viewBox={`0 0 ${SZ} ${SZ}`}
        className="h-36 w-36 cursor-pointer touch-none select-none"
        data-testid="wind-dial"
        onMouseDown={(e) => {
          setDragging(true);
          setFromEvent(e.clientX, e.clientY);
        }}
        onTouchStart={(e) => {
          setDragging(true);
          const t = e.touches[0];
          setFromEvent(t.clientX, t.clientY);
        }}
      >
        <defs>
          <marker id="windArrow" markerWidth="8" markerHeight="8" refX="5" refY="3" orient="auto">
            <path d="M0,0 L6,3 L0,6 Z" fill="#cc0000" />
          </marker>
        </defs>
        <circle cx={C} cy={C} r={RIM + 12} fill="#0d0f12" stroke="#333842" strokeWidth="1" />
        <circle cx={C} cy={C} r={RIM} fill="none" stroke="#262b33" strokeWidth="1" />
        {/* tick ring */}
        {Array.from({ length: 12 }).map((_, i) => {
          const a = (i * 30 * Math.PI) / 180;
          const x1 = C + Math.sin(a) * (RIM + 6);
          const y1 = C - Math.cos(a) * (RIM + 6);
          const x2 = C + Math.sin(a) * (RIM + 11);
          const y2 = C - Math.cos(a) * (RIM + 11);
          return <line key={i} x1={x1} y1={y1} x2={x2} y2={y2} stroke="#3f454f" strokeWidth="1.5" />;
        })}
        {marks.map((m) => (
          <text
            key={m.label}
            x={m.x}
            y={m.y}
            textAnchor="middle"
            className="fill-zinc-500"
            style={{ fontSize: 9, fontWeight: 700 }}
          >
            {m.label}
          </text>
        ))}
        {/* wind flow arrow (from source through centre) */}
        <line x1={sx} y1={sy} x2={ox} y2={oy} stroke="#cc0000" strokeWidth="3" markerEnd="url(#windArrow)" />
        <circle cx={sx} cy={sy} r="4" fill="#cc0000" />
        {/* centre = shooter */}
        <circle cx={C} cy={C} r="3" fill="#71717a" />
        <text x={C} y={C + 26} textAnchor="middle" className="fill-zinc-100" style={{ fontSize: 15, fontWeight: 800, fontFamily: "monospace" }}>
          {Math.round(value)}°
        </text>
      </svg>
      <div className="mt-1 text-[10px] font-bold uppercase tracking-widest text-dase-red">{clock}</div>
    </div>
  );
};

export default WindDial;
